// Módulos opcionales del negocio: dependencias, activar/desactivar y permisos funcionales
// (DOMAIN §12, MOD-01…06). Apagar un módulo no borra datos (MOD-04).
import { fmtARS } from './format';
import { error, warning, type Issue } from './issues';
import type { ModuleKey, Pesos } from './types';
import { MODULE_KEYS } from './types';

export type ModuleState = Record<ModuleKey, boolean>;

export interface ModuleInfo {
  /** Nombre del módulo (MOD-02). */
  label: string;
  /** Nombre corto para mensajes como "{Módulo} está desactivado." (API §1.6). */
  shortLabel: string;
  /** Módulos que tienen que estar activos para usarlo. */
  requires: readonly ModuleKey[];
}

/** MOD-02. */
export const MODULES: Readonly<Record<ModuleKey, Readonly<ModuleInfo>>> = Object.freeze({
  sales: Object.freeze({ label: 'Caja y cobros', shortLabel: 'Caja y cobros', requires: Object.freeze([]) }),
  stock: Object.freeze({
    label: 'Stock y productos',
    shortLabel: 'Stock y productos',
    requires: Object.freeze([]),
  }),
  commissions: Object.freeze({
    label: 'Comisiones y liquidaciones',
    shortLabel: 'Comisiones',
    requires: Object.freeze(['sales'] as const),
  }),
  giftcards: Object.freeze({
    label: 'Gift cards',
    shortLabel: 'Gift cards',
    requires: Object.freeze(['sales'] as const),
  }),
  finance: Object.freeze({
    label: 'Finanzas',
    shortLabel: 'Finanzas',
    requires: Object.freeze(['sales'] as const),
  }),
  dashboard: Object.freeze({
    label: 'Tablero',
    shortLabel: 'Tablero',
    requires: Object.freeze(['sales'] as const),
  }),
});

/** MOD-01: lo que está siempre activo (no se apaga). */
export const CORE_AREAS: readonly string[] = Object.freeze([
  'Agenda',
  'Clientes',
  'Reservas online',
  'Equipo y servicios',
  'Ajustes',
  'Ayuda',
]);

/** Todos los módulos activos (estado de un negocio nuevo con todo prendido). */
export function allModules(on = true): ModuleState {
  return { sales: on, stock: on, commissions: on, giftcards: on, finance: on, dashboard: on };
}

/** Módulos que dependen (directa o indirectamente) de `key`, en el orden de `MODULE_KEYS`. */
export function dependentsOf(key: ModuleKey): ModuleKey[] {
  const out = new Set<ModuleKey>();
  let grew = true;
  while (grew) {
    grew = false;
    for (const k of MODULE_KEYS) {
      if (out.has(k) || k === key) continue;
      if (MODULES[k].requires.some((r) => r === key || out.has(r))) {
        out.add(k);
        grew = true;
      }
    }
  }
  return MODULE_KEYS.filter((k) => out.has(k));
}

/** Módulos que `key` necesita (directa o indirectamente), en el orden de `MODULE_KEYS`. */
export function requirementsOf(key: ModuleKey): ModuleKey[] {
  const out = new Set<ModuleKey>();
  const visit = (k: ModuleKey): void => {
    for (const r of MODULES[k].requires) {
      if (!out.has(r)) {
        out.add(r);
        visit(r);
      }
    }
  };
  visit(key);
  return MODULE_KEYS.filter((k) => out.has(k));
}

/**
 * ¿El módulo está en uso? Prendido **y** con todo lo que requiere prendido (un estado
 * inconsistente, p. ej. `commissions` sin `sales`, cuenta como apagado).
 */
export function isModuleActive(state: Readonly<ModuleState>, key: ModuleKey): boolean {
  return !!state[key] && requirementsOf(key).every((r) => !!state[r]);
}

/** MOD-03: activar `key` activa también lo que requiere. */
export function enableModule(
  state: Readonly<ModuleState>,
  key: ModuleKey,
): { next: ModuleState; alsoEnabled: ModuleKey[] } {
  const next: ModuleState = { ...state };
  const alsoEnabled = requirementsOf(key).filter((r) => !state[r]);
  for (const r of alsoEnabled) next[r] = true;
  next[key] = true;
  return { next, alsoEnabled };
}

/** MOD-03: desactivar `key` desactiva también lo que depende de él. */
export function disableModule(
  state: Readonly<ModuleState>,
  key: ModuleKey,
): { next: ModuleState; alsoDisabled: ModuleKey[] } {
  const next: ModuleState = { ...state };
  const alsoDisabled = dependentsOf(key).filter((d) => !!state[d]);
  for (const d of alsoDisabled) next[d] = false;
  next[key] = false;
  return { next, alsoDisabled };
}

/** Une etiquetas en español: "a", "a y b", "a, b y c". */
function joinList(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1] ?? ''}`;
}

export interface ModuleChangeContext {
  /** Gift cards vigentes con saldo (para apagar `giftcards`). */
  liveGiftCards?: { count: number; balance: Pesos };
  /** Comisiones y propinas pendientes de liquidar (para apagar `commissions`). */
  pendingCommissions?: Pesos;
}

/**
 * Advertencias para confirmar (GEN-60) al activar o desactivar un módulo (MOD-03):
 * `modules_also_enabled`, `modules_also_disabled`, `giftcards_live_balance`, `commissions_pending`.
 */
export function moduleChangeWarnings(
  state: Readonly<ModuleState>,
  key: ModuleKey,
  enable: boolean,
  ctx: ModuleChangeContext = {},
): Issue[] {
  const out: Issue[] = [];
  if (enable) {
    const { alsoEnabled } = enableModule(state, key);
    if (alsoEnabled.length) {
      out.push(
        warning('modules_also_enabled', {
          module: MODULES[key].label,
          required: joinList(alsoEnabled.map((k) => MODULES[k].label)),
        }),
      );
    }
    return out;
  }
  const { alsoDisabled } = disableModule(state, key);
  if (alsoDisabled.length) {
    out.push(warning('modules_also_disabled', { list: joinList(alsoDisabled.map((k) => MODULES[k].label)) }));
  }
  const turnedOff = new Set<ModuleKey>([key, ...alsoDisabled]);
  const gc = ctx.liveGiftCards;
  if (turnedOff.has('giftcards') && state.giftcards && gc && gc.count > 0) {
    out.push(warning('giftcards_live_balance', { n: gc.count, amount: fmtARS(gc.balance) }));
  }
  const pending = ctx.pendingCommissions ?? 0;
  if (turnedOff.has('commissions') && state.commissions && pending > 0) {
    out.push(warning('commissions_pending', { amount: fmtARS(pending) }));
  }
  return out;
}

/**
 * MOD-04: `[403 module_disabled] "{Módulo} está desactivado. Se activa en Ajustes › Módulos."`.
 * `module` es el nombre que va en el mensaje; `moduleKey` es la clave que la API pone en `details.module` (API §1.7).
 */
export function moduleDisabledIssue(key: ModuleKey): Issue {
  return error('module_disabled', { module: MODULES[key].shortLabel, moduleKey: key });
}

export interface ModuleFeatures {
  canCharge: boolean;
  closeAsAttended: boolean;
  sellProducts: boolean;
  recipeConsumption: boolean;
  commissionsActive: boolean;
  giftCardsActive: boolean;
  financeActive: boolean;
  dashboardActive: boolean;
  stockActive: boolean;
}

/** MOD-06: permisos funcionales derivados de los módulos. */
export function features(state: Readonly<ModuleState>): ModuleFeatures {
  const sales = !!state.sales;
  const stock = !!state.stock;
  return {
    canCharge: sales,
    closeAsAttended: !sales,
    sellProducts: sales && stock,
    recipeConsumption: sales && stock,
    commissionsActive: sales && !!state.commissions,
    giftCardsActive: sales && !!state.giftcards,
    financeActive: sales && !!state.finance,
    dashboardActive: sales && !!state.dashboard,
    stockActive: stock,
  };
}
